import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchProjectEpisodeVersions,
  fetchRestoreProjectEpisodeVersion,
} from '@/service/api/project';
import { formatDate } from './utils';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';

interface EpisodeVersionsModalProps {
  open: boolean;
  projectId: string;
  epNo: number | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

export default function EpisodeVersionsModal({
  open,
  projectId,
  epNo,
  onClose,
  onSuccess,
  onError,
}: EpisodeVersionsModalProps) {
  const [versions, setVersions] = useState<Api.Project.EpisodeFileVersionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);

  // 预览状态
  const [previewMedia, setPreviewMedia] = useState<{ title: string; url: string } | null>(null);

  const loadEpisodeVersions = useCallback(async () => {
    if (!projectId || epNo === null) return;
    setIsLoading(true);
    try {
      const res = await fetchProjectEpisodeVersions(projectId, epNo);
      setVersions(res || []);
    } catch (err) {
      onError(err instanceof Error ? err.message : '获取历史版本失败');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, epNo, onError]);

  useEffect(() => {
    if (open && epNo !== null) {
      loadEpisodeVersions();
    } else {
      setVersions([]);
    }
  }, [open, epNo, loadEpisodeVersions]);

  if (!open || epNo === null) return null;

  /** 恢复指定历史版本 */
  const handleRestoreVersion = async (versionId: string) => {
    if (!versionId || epNo === null) return;
    setRestoringVersionId(versionId);
    try {
      await fetchRestoreProjectEpisodeVersion(projectId, epNo, versionId);
      onSuccess(`第 ${epNo} 集历史版本恢复成功！`);
      loadEpisodeVersions();
    } catch (err) {
      onError(err instanceof Error ? err.message : '版本恢复失败');
    } finally {
      setRestoringVersionId(null);
    }
  };

  /** 状态 Badge */
  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'PASSED':
      case 'APPROVED':
        return <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">审核通过</span>;
      case 'PENDING':
      case 'PENDING_REVIEW':
      case 'SUBMITTED':
        return <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">待审核</span>;
      case 'REJECTED':
        return <span className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">已驳回</span>;
      case 'REPLACED':
        return <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">已替换</span>;
      default:
        return <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">{status || '处理中'}</span>;
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
          <h4 className="font-bold text-foreground-900 text-base flex items-center gap-2">
            <i className="ri-history-line text-primary-600"></i>
            第 {epNo} 集 - 历史版本记录
          </h4>
          <button
            onClick={onClose}
            className="text-foreground-400 hover:text-foreground-700 text-lg transition-colors"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-foreground-400 text-xs">
            <i className="ri-loader-4-line animate-spin text-xl block mb-2 text-primary-500"></i>
            获取历史版本...
          </div>
        ) : versions.length === 0 ? (
          <div className="py-12 text-center text-foreground-400 text-xs">
            暂无更早的历史版本记录
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {versions.map((ver, idx) => {
              const isCurrent = ver.is_current ?? (idx === 0);
              const versionId = ver.id || '';
              const isRestoringThis = restoringVersionId === versionId;
              const accessUrl = ver.upload_asset?.access_url;

              return (
                <div
                  key={versionId || idx}
                  className={`p-3.5 rounded-xl border transition-all text-xs ${
                    isCurrent ? 'bg-primary-50/50 border-primary-200' : 'bg-gray-50/60 border-gray-200/70'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-foreground-900">
                        版本 V{ver.version_no ?? (versions.length - idx)}
                      </span>
                      {isCurrent && (
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-primary-500 text-foreground-950">
                          当前使用中
                        </span>
                      )}
                      {renderStatusBadge(ver.status)}
                    </div>
                    <span className="text-[11px] text-foreground-400">
                      {formatDate(ver.created_at || ver.replaced_at)}
                    </span>
                  </div>

                  {ver.upload_asset && (
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-foreground-600 truncate">
                        文件名: {ver.upload_asset.file_name || ver.upload_asset_id || '-'}
                      </p>
                      {accessUrl && (
                        <button
                          type="button"
                          onClick={() =>
                            setPreviewMedia({
                              title: `第 ${epNo} 集 - 版本 V${ver.version_no ?? (versions.length - idx)}`,
                              url: accessUrl,
                            })
                          }
                          className="text-[11px] text-primary-600 hover:underline flex items-center gap-0.5 shrink-0 cursor-pointer"
                        >
                          <i className="ri-play-circle-line"></i> 在线预览
                        </button>
                      )}
                    </div>
                  )}

                  {ver.rejection_reason && (
                    <p className="text-rose-600 text-[11px] mb-2">驳回原因: {ver.rejection_reason}</p>
                  )}

                  {!isCurrent && versionId && (
                    <div className="mt-2 pt-2 border-t border-gray-200/50 flex justify-end">
                      <button
                        disabled={isRestoringThis}
                        onClick={() => handleRestoreVersion(versionId)}
                        className="px-3 py-1 rounded bg-white hover:bg-primary-50 border border-primary-300 text-primary-700 font-medium text-xs transition-all disabled:opacity-50 flex items-center gap-1 cursor-pointer"
                      >
                        {isRestoringThis && <i className="ri-loader-4-line animate-spin"></i>}
                        <i className="ri-restart-line"></i> 恢复为此版本
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 pt-3 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-100 text-foreground-700 text-xs hover:bg-gray-200 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>

      {/* 历史版本在线视频预览 Modal */}
      <PortfolioPreviewModal
        open={previewMedia !== null}
        title={previewMedia?.title || '剧集历史版本预览'}
        isVideo={true}
        mediaUrl={previewMedia?.url}
        onClose={() => setPreviewMedia(null)}
      />
    </div>,
    document.body
  );
}
